import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/models/api-response.model';
import { DepartamentoEmpresa, DepartamentoOpcao, PageResponse } from 'src/app/pages/empresa/pages/departamentos/models/departamento.model';
import { Colaborador } from 'src/app/pages/pessoas/models/colaborador.model';
import { unwrapConteudo } from '../utils/api.utils';

@Injectable({
  providedIn: 'root'
})
export class DepartamentosApiService {
  private readonly baseUrl = environment.apiBaseUrl;
  private cacheAtivos$: Observable<DepartamentoOpcao[]> | null = null;

  constructor(private http: HttpClient) {}

  listar(page: number, size: number, nome?: string): Observable<PageResponse<DepartamentoEmpresa>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (nome?.trim()) {
      params = params.set('nome', nome.trim());
    }

    return this.http.get<PageResponse<DepartamentoEmpresa>>(`${this.baseUrl}/api/v1/departamentos`, { params })
      .pipe(map((response) => this.normalizePage(response, size)));
  }

  criar(nome: string): Observable<DepartamentoEmpresa> {
    const payload = { nome: nome.trim() };
    return this.http.post<ApiResponse<DepartamentoEmpresa>>(`${this.baseUrl}/api/v1/departamentos`, payload)
      .pipe(tap(() => this.invalidarCacheAtivos()), map((res) => this.normalizeDepartamento(unwrapConteudo(res))));
  }

  editar(id: number, payload: { nome: string; status: 'ATIVO' | 'INATIVO' }): Observable<DepartamentoEmpresa> {
    const body = {
      nome: payload.nome.trim(),
      status: payload.status
    };
    return this.http.put<ApiResponse<DepartamentoEmpresa>>(`${this.baseUrl}/api/v1/departamentos/${id}`, body)
      .pipe(tap(() => this.invalidarCacheAtivos()), map((res) => this.normalizeDepartamento(unwrapConteudo(res))));
  }

  excluir(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/v1/departamentos/${id}`)
      .pipe(tap(() => this.invalidarCacheAtivos()));
  }

  /**
   * Bate `GET /api/v1/departamentos/opcoes` (Bloco 4 Node — 2026-07-28).
   *
   * CONTRATO: retorna SO ATIVOS por definicao do endpoint + payload SLIM
   * `{id, nome}` (sem `status`, `padrao`, `colaboradores`, timestamps).
   * BUG-004 (2026-07-29) foi causado por filtro client `d.status === 'ATIVO'`
   * sobre esse payload — `status` e undefined no slim, filter zerava a lista.
   * Tipagem `DepartamentoOpcao[]` faz o TS acusar acesso a campos removidos.
   */
  listarAtivos(): Observable<DepartamentoOpcao[]> {
    if (!this.cacheAtivos$) {
      this.cacheAtivos$ = this.http
        .get<DepartamentoOpcao[] | ApiResponse<DepartamentoOpcao[]>>(
          `${this.baseUrl}/api/v1/departamentos/opcoes`
        )
        .pipe(
          map((res) => {
            const lista: DepartamentoOpcao[] = Array.isArray(res)
              ? res
              : (unwrapConteudo(res as ApiResponse<DepartamentoOpcao[]>) ?? []);
            return lista.map((dep) => ({ id: dep.id, nome: dep.nome }));
          }),
          shareReplay(1)
        );
    }
    return this.cacheAtivos$;
  }

  /** Invalida o cache de departamentos ativos (chamar após criar/editar/excluir departamento). */
  invalidarCacheAtivos(): void {
    this.cacheAtivos$ = null;
  }

  obterGestores(departamentoId: number): Observable<Colaborador[]> {
    return this.http.get<ApiResponse<Colaborador[]>>(
      `${this.baseUrl}/api/v1/departamentos/${departamentoId}/usuarios-vinculados`
    ).pipe(map((res) => unwrapConteudo(res) ?? []));
  }

  private normalizeDepartamento(dep: DepartamentoEmpresa): DepartamentoEmpresa {
    return {
      ...dep,
      colaboradores: dep.colaboradores ?? 0,
      avatarUrl: dep.avatarUrl ?? null
    };
  }

  private normalizePage(response: PageResponse<DepartamentoEmpresa>, fallbackSize: number): PageResponse<DepartamentoEmpresa> {
    return {
      ...response,
      size: response.size ?? fallbackSize,
      content: (response.content ?? []).map((dep) => this.normalizeDepartamento(dep))
    };
  }

}
